// Lightweight guided tour for the Generate page. Renders a dimmed
// backdrop that "cuts out" the current step's target element (4 rects
// forming a frame around the target's bounding box), a yellow halo
// around the target, and a tooltip card with Next / Back / Skip
// controls. Steps are CSS-selector driven: targets carry a
// `data-tutorial="..."` attribute and the overlay finds them with
// querySelector. Missing targets fall back to a centered tooltip so
// the tour still moves forward instead of getting stuck.
//
// Step content lives in VIDEO_STEPS / IMAGE_STEPS below so the tour
// text is easy to find and edit without digging through GenerateView.

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Y, T, LABEL_FONT } from "../theme";

export const VIDEO_STEPS = [
  {
    selector: '[data-tutorial="mode-tabs"]',
    title: "Video or Image Mode",
    body:
      "These tabs at the top of the page switch between Video and Image generation. The form below adapts to your choice. This tour walks through the Video form - switch to Image and open the tutorial again to see that one.",
  },
  {
    selector: '[data-tutorial="video-model"]',
    title: "Pick a Video Model",
    body:
      "Open this picker to choose your video model. Different models have different resolutions, speed, audio support, and per-second pricing - the Recommended row at the top is a good starting point. Kling 3.0 Omni is the default and supports references, end frames, audio, and text-only.",
  },
  {
    selector: '[data-tutorial="aspect-ratio"]',
    title: "Aspect Ratio",
    body:
      "Pick the output ratio. Auto matches your source image when applicable; otherwise the output is rendered at the chosen aspect (any source image is cropped to fit, which you can position with the crop overlay on the frame tile).",
  },
  {
    selector: '[data-tutorial="duration"]',
    title: "Duration & Audio",
    body:
      "Length of the generated video in seconds (cost scales with duration on most models), plus the audio toggle when the model supports it. The price in the Generate button updates live as you change these.",
  },
  {
    selector: '[data-tutorial="input-mode"]',
    title: "Image Input Mode",
    body:
      'Pick how you want to give the model visual input - "References" for multi-image guidance, or "Start / End Frame" for keyframes. Models that support text-only can leave the drop empty; you\'ll get a confirm dialog at submit. If a model doesn\'t support References, this toggle locks to Start / End Frame.',
  },
  {
    selector: '[data-tutorial="drop-zone"]',
    title: "Upload Your Images",
    body:
      "Drop or click these tiles to add your start / end frames or reference images. Kling models validate size and aspect ratio up front - the hint text below the tiles spells out the rules, and bad uploads get a red border + warning triangle so you fix them before submitting.",
  },
  {
    selector: '[data-tutorial="prompt"]',
    title: "Describe Your Video",
    placement: "above",
    body:
      "Write what you want generated. Specific details about subject, action, style, lighting, and camera movement produce stronger results. On Omni models that accept reference tokens, you'll see colored chips above the prompt that insert the right syntax at the cursor.",
  },
  {
    selector: '[data-tutorial="generate"]',
    title: "Generate",
    placement: "above",
    body:
      "Click to submit the job. The button shows the credit cost and is disabled with a tooltip if anything's missing or invalid. Submitted jobs appear in the job list below as they process, and in the Library tab once they're done.",
  },
  {
    selector: '[data-tutorial="jobs"]',
    title: "Your Job Queue",
    placement: "above",
    body:
      "After you hit Generate, the job lands here and updates live - queued, then processing with an elapsed timer, then ready to play. Click a card to expand its details; finished videos also appear in the Library tab.",
  },
];

export const IMAGE_STEPS = [
  {
    selector: '[data-tutorial="mode-tabs"]',
    title: "Video or Image Mode",
    body:
      "These tabs at the top of the page switch between Video and Image generation. The form below adapts to your choice. This tour walks through the Image form - switch to Video and open the tutorial again for that one.",
  },
  {
    selector: '[data-tutorial="image-model"]',
    title: "Pick an Image Model",
    body:
      "Open this picker to choose your image model. The list mixes raster models (photoreal, stylized, edit) and vector models (SVG output). Each model card shows resolution, max references, and price per image. The Recommended row at the top is a good starting point.",
  },
  {
    selector: '[data-tutorial="image-output"]',
    title: "Output Settings",
    body:
      "Pick resolution, aspect ratio, and how many images to generate at once. The cost in the Generate button below scales with all three.",
  },
  {
    selector: '[data-tutorial="image-refs"]',
    title: "References (Optional)",
    body:
      "Drop or click to add reference images. Add references to do image-to-image editing or multi-image fusion; leave empty to generate from your prompt alone (text-to-image). The colored borders match the colored chips above the prompt that insert reference tokens at the cursor.",
  },
  {
    selector: '[data-tutorial="prompt"]',
    title: "Describe Your Image",
    placement: "above",
    body:
      "Write what you want generated. Specific details about subject, style, lighting, and composition produce stronger results. Most image models also accept a negative prompt (things to avoid) further down the form.",
  },
  {
    selector: '[data-tutorial="generate"]',
    title: "Generate",
    placement: "above",
    body:
      "Click to submit the job. The button shows the credit cost and is disabled with a tooltip if anything's missing. Generated images appear in the job list below as they're produced, and in the Library tab once they're done.",
  },
  {
    selector: '[data-tutorial="jobs"]',
    title: "Your Job Queue",
    placement: "above",
    body:
      "After you hit Generate, the job lands here and updates live as it's produced. Click a card to expand it and preview or download the outputs; finished images also appear in the Library tab.",
  },
];

// Re-measure the target every PROBE_INTERVAL ms in case the page
// reflows underneath (image loads, toggles flip, scroll changes the
// bounding box). 200ms keeps it smooth without being expensive.
const PROBE_INTERVAL = 200;
const HALO_PAD = 6;       // px of glow ring outside the halo border
const HALO_OFFSET = 8;    // px gap between the target edge and the halo border
const TIP_WIDTH = 340;
const TIP_GAP = 12;       // px between target and tooltip
const VIEWPORT_MARGIN = 8;
const BACKDROP = "rgba(0,0,0,0.6)";

export function TutorialOverlay({ steps, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[stepIdx] || null;
  const total = steps.length;
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === total - 1;

  const next = useCallback(() => {
    if (isLast) onClose();
    else setStepIdx((i) => i + 1);
  }, [isLast, onClose]);
  const prev = useCallback(() => {
    if (!isFirst) setStepIdx((i) => i - 1);
  }, [isFirst]);

  // Re-measure the target on step change + on resize / scroll / DOM
  // reflow. setInterval is the cheap catch-all for layout shifts the
  // resize / scroll listeners don't fire on (e.g. fonts loading,
  // sibling content showing/hiding).
  const scrolledRef = useRef(false);
  useEffect(() => {
    scrolledRef.current = false;
    let raf;
    const compute = () => {
      // A selector may match MULTIPLE elements (e.g. the job-queue step
      // marks both the header row and the first card's top row); the halo
      // wraps the UNION of their boxes so it hugs just that region instead
      // of the whole list. Single-match steps behave exactly as before.
      const els = step?.selector ? Array.from(document.querySelectorAll(step.selector)) : [];
      const boxes = els
        .map(el => el.getBoundingClientRect())
        .filter(r => r.width > 0 || r.height > 0); // skip display:none (0x0)
      if (boxes.length === 0) { setRect(null); return; }
      const left = Math.min(...boxes.map(b => b.left));
      const top = Math.min(...boxes.map(b => b.top));
      const right = Math.max(...boxes.map(b => b.right));
      const bottom = Math.max(...boxes.map(b => b.bottom));
      // Scroll the first target into view exactly once per step; doing it
      // on every measurement fights the user when they manually scroll.
      if (!scrolledRef.current && (bottom < 0 || top > window.innerHeight - 100)) {
        scrolledRef.current = true;
        els[0].scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setRect({ left, top, width: right - left, height: bottom - top, bottom, right });
    };
    compute();
    const onResize = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(compute); };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, { capture: true });
    const id = setInterval(compute, PROBE_INTERVAL);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, { capture: true });
      clearInterval(id);
      cancelAnimationFrame(raf);
    };
  }, [stepIdx, step]);

  // Keyboard: Esc closes; arrows / Enter advance.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  if (!step) return null;

  // Tooltip placement: prefer below the target; flip above if there
  // isn't enough room; fall back to center when the target is missing.
  let tipStyle;
  if (!rect) {
    tipStyle = {
      position: "fixed",
      left: "50%", top: "50%", transform: "translate(-50%, -50%)",
      width: TIP_WIDTH, maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
    };
  } else {
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const tipApproxHeight = 220;
    // A step can force its tooltip side via placement: "above" | "below";
    // otherwise pick whichever side has more room.
    const placeBelow = step.placement === "above" ? false
      : step.placement === "below" ? true
      : (spaceBelow >= tipApproxHeight || spaceBelow >= spaceAbove);
    const desiredLeft = rect.left + rect.width / 2 - TIP_WIDTH / 2;
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(desiredLeft, window.innerWidth - TIP_WIDTH - VIEWPORT_MARGIN),
    );
    if (placeBelow) {
      tipStyle = { position: "fixed", left, top: rect.bottom + TIP_GAP, width: TIP_WIDTH };
    } else {
      tipStyle = { position: "fixed", left, bottom: window.innerHeight - rect.top + TIP_GAP, width: TIP_WIDTH };
    }
  }

  // Backdrop = 4 rects covering the viewport minus the target's box,
  // so the target itself stays bright while everything else dims. The
  // halo div sits just outside the target with pointerEvents:none so
  // it doesn't intercept clicks on whatever's underneath.
  return ReactDOM.createPortal((
    <div style={{ position: "fixed", inset: 0, zIndex: 9998 }}>
      {rect ? (
        <>
          {/* Top */}
          <div onClick={onClose} style={{ position: "fixed", left: 0, top: 0, right: 0, height: Math.max(0, rect.top - HALO_OFFSET - HALO_PAD), background: BACKDROP }}/>
          {/* Bottom */}
          <div onClick={onClose} style={{ position: "fixed", left: 0, top: rect.bottom + HALO_OFFSET + HALO_PAD, right: 0, bottom: 0, background: BACKDROP }}/>
          {/* Left */}
          <div onClick={onClose} style={{ position: "fixed", left: 0, top: Math.max(0, rect.top - HALO_OFFSET - HALO_PAD), width: Math.max(0, rect.left - HALO_OFFSET - HALO_PAD), height: rect.height + 2 * (HALO_OFFSET + HALO_PAD), background: BACKDROP }}/>
          {/* Right */}
          <div onClick={onClose} style={{ position: "fixed", left: rect.right + HALO_OFFSET + HALO_PAD, top: Math.max(0, rect.top - HALO_OFFSET - HALO_PAD), right: 0, height: rect.height + 2 * (HALO_OFFSET + HALO_PAD), background: BACKDROP }}/>
          {/* Halo */}
          <div style={{
            position: "fixed",
            left: rect.left - HALO_OFFSET,
            top: rect.top - HALO_OFFSET,
            width: rect.width + 2 * HALO_OFFSET,
            height: rect.height + 2 * HALO_OFFSET,
            border: `2px solid #FF6FB5`,
            borderRadius: 10,
            pointerEvents: "none",
            boxShadow: `0 0 0 ${HALO_PAD}px #FF6FB526, 0 0 24px #FF6FB555`,
          }}/>
        </>
      ) : (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: BACKDROP }}/>
      )}
      {/* Tooltip card */}
      <div role="dialog" aria-modal="true" aria-label={step.title}
        style={{
          ...tipStyle,
          background: T.popoverBg,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: 14,
          boxShadow: "0 10px 40px rgba(0,0,0,0.55)",
          color: T.text2,
        }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontFamily: LABEL_FONT, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: T.text3 }}>
            Tutorial · {stepIdx + 1} / {total}
          </span>
          <button onClick={onClose} title="Close tutorial (Esc)"
            style={{ background: "transparent", border: "none", color: T.text3, cursor: "pointer", padding: 4, display: "flex", borderRadius: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.text3)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ color: T.title, fontSize: 15, fontWeight: 800, marginBottom: 6, letterSpacing: 0.2 }}>{step.title}</div>
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>{step.body}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 14 }}>
          <button onClick={onClose}
            style={{ background: "transparent", border: "none", color: T.text3, cursor: "pointer", fontSize: 11, fontFamily: LABEL_FONT, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, padding: "4px 6px" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.text2)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.text3)}>
            Skip Tour
          </button>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={prev} disabled={isFirst}
              style={{
                padding: "6px 12px", borderRadius: 16,
                background: "transparent",
                border: `1px solid ${T.overlay12}`,
                color: isFirst ? T.text3 : T.text2,
                opacity: isFirst ? 0.45 : 1,
                cursor: isFirst ? "not-allowed" : "pointer",
                fontSize: 11, fontFamily: LABEL_FONT, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600,
                transition: "all 0.15s",
              }}>
              Back
            </button>
            <button onClick={next}
              style={{
                padding: "6px 14px", borderRadius: 16,
                background: '#FF6FB5', border: "none", color: T.dark,
                cursor: "pointer",
                fontSize: 11, fontFamily: LABEL_FONT, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 800,
                transition: "all 0.15s",
              }}>
              {isLast ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
